import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';

// Import the raw content as a string
const tosContent = `# Divscore Terms of Service

**Effective Date:** January 1st, 2025

Welcome to Divscore, a service offered by **Guernica LLC** ("Company," "we," "us," or "our"), a Wyoming Limited Liability Company. By accessing or using [Divscore.io](https://divscore.io) (the "Website"), our related services, tools, and any other applications or platforms (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms" or "Agreement"). If you do not agree, please do not access or use the Service.

---

## 1. Description of Service

Divscore provides financial information and analytics, including but not limited to:

- **Scoring** of investment portfolios  
- **Market comparisons**  
- **Econometric and financial analysis** based on academic research (including Nobel Prize frameworks)  
- **Guidance and recommendations** based on our scoring, relative to a "market portfolio"  

Our goal is to help users better understand certain investment metrics and portfolio performance. The Service is provided for **informational and educational purposes only**.

---

## 2. Eligibility

1. **Age Requirement**: You must be at least 18 years of age (or the age of majority in your jurisdiction) to use the Service. By accessing or using the Service, you represent and warrant that you meet this requirement.

2. **Jurisdiction**: You are responsible for ensuring that your use of the Service complies with all laws, rules, and regulations applicable to you, including those in your jurisdiction of residence.

---

## 3. No Investment or Financial Advice

1. **Not a Registered Advisor**: Guernica LLC (Divscore) is **not** a registered investment advisor, broker-dealer, or financial planner. We do **not** provide personalized investment or financial advice.

2. **Educational Purposes Only**: All information presented is for **educational and informational purposes**. It should not be construed as professional advice or a recommendation to buy, sell, or hold any security or financial instrument.

3. **Independent Verification**: You should independently verify all information received through the Service before making any investment decisions. We strongly recommend consulting with a qualified financial advisor, accountant, or legal professional.

4. **No Fiduciary Relationship**: Your use of the Service does not create any fiduciary relationship between you and Guernica LLC.

---

## 4. User Accounts

1. **Registration**: To access certain features (including subscription services), you may need to create an account. You agree to provide accurate, current, and complete information during registration and to keep it updated.

2. **Account Security**: You are responsible for safeguarding your account credentials (username/password) and for all activities that occur under your account. Notify us immediately if you suspect any unauthorized use of your account.

3. **Account Termination**: We reserve the right to suspend or terminate accounts that violate these Terms or that pose security risks to the Service.

---

## 5. Subscription and Payment Terms

1. **Subscription Plans**: We may offer both free and paid subscription plans. Details of subscription tiers, features, and pricing are available on our Website.

2. **Billing**: If you subscribe to a paid plan, you agree to pay all applicable fees in accordance with the selected subscription (e.g., monthly or annual billing).  
   - **Payment Method**: You will provide a valid payment method, and you authorize us to charge fees to that payment method.  
   - **Billing Cycle**: Unless otherwise indicated, subscription fees recur automatically at the end of each billing cycle.

3. **Refunds**:  
   - **Refund Policy**: Subscription fees are generally **non-refundable**. However, we may issue refunds or credits in extraordinary circumstances at our sole discretion.  
   - **Cancellation**: You may cancel your subscription at any time, but you will continue to have access to the paid features until the end of your current billing cycle.

4. **Price Changes**: We reserve the right to change our subscription fees at any time. Any fee change will take effect at the start of the next billing cycle after we provide notice to you.

---

## 6. Collection and Use of Data

1. **Personal Financial Data**: When you use the Service, we may collect data about your financial holdings, portfolio preferences, and other relevant information ("Personal Financial Data"). This allows us to generate scores, comparisons, and other Service features.

2. **Data Privacy**: Your use of the Service is governed by our [Privacy Policy](/privacy), which is incorporated into these Terms by reference. The Privacy Policy describes how we collect, use, share, and protect your data, including any Personal Financial Data.

3. **Consent**: By submitting Personal Financial Data, you warrant that you have the right to provide such data and grant us permission to process it in accordance with our Privacy Policy and these Terms.

4. **Regulatory Compliance**: We make reasonable efforts to comply with applicable data protection regulations (such as Wyoming, U.S. federal laws, and possibly international laws like GDPR if we have users in the EU). However, you are responsible for ensuring that your use of the Service complies with the data laws applicable to you.

---

## 7. Intellectual Property

1. **Ownership**: All content, software, designs, trademarks, logos, and other intellectual property related to the Service (collectively, "IP") are owned or licensed by Guernica LLC.

2. **Limited License**: Subject to your compliance with these Terms, we grant you a non-exclusive, non-transferable, revocable license to access and use the Service for your personal, non-commercial use.

3. **Restrictions**: You agree not to  
   - Copy, modify, or create derivative works based on the Service's IP;  
   - Reverse engineer, disassemble, or otherwise attempt to derive source code from the Service;  
   - Use the Service to violate any law or regulation.

---

## 8. Disclaimers

1. **"As Is" and "As Available"**: The Service is provided on an "as is" and "as available" basis, without any warranties of any kind, express or implied.

2. **No Guarantee of Accuracy**: While we strive for accuracy and reliability, we **do not warrant** that any information provided through the Service is complete, reliable, or error-free.

3. **No Guarantee of Results**: Neither current nor past performance in any investment is indicative of future results. We make **no** guarantees or predictions regarding performance, profitability, or earnings.

4. **User Responsibility**: You assume sole responsibility for any decisions or actions you take based on the information available through the Service.

---

## 9. Limitation of Liability

1. **Indirect Damages**: To the fullest extent permitted by law, Guernica LLC and its affiliates, officers, directors, employees, or agents are **not** liable for any indirect, incidental, special, consequential, or punitive damages (including loss of profits or data) arising out of or related to your use of the Service.

2. **Cap on Liability**: In no event shall the aggregate liability of Guernica LLC exceed the greater of (a) the total fees you paid (if any) for accessing the Service in the twelve (12) months prior to the date of the claim, or (b) **USD \$100**.

3. **State-Specific Exceptions**: Some states do not allow the exclusion or limitation of certain damages. If these laws apply to you, some or all of the above exclusions or limitations may not apply to you, and you may have additional rights.

---

## 10. Indemnification

You agree to indemnify, defend, and hold harmless Guernica LLC, its affiliates, and their respective officers, directors, employees, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:

- Your breach of these Terms,  
- Your violation of any law or the rights of a third party, or  
- Your use or misuse of the Service, including your reliance on any information or content obtained through the Service.

---

## 11. Third-Party Services and Links

The Service may contain links to third-party websites or resources. These links are provided for your convenience only. We do not endorse, control, or assume any responsibility for third-party sites, content, or services. Your use of these third-party resources is at your own risk and subject to the terms and conditions of those third parties.

---

## 12. Modification and Termination

1. **Changes to Terms**: We may revise these Terms from time to time. The most current version will be posted on our Website. If you continue to use the Service after the changes become effective, you agree to be bound by the revised Terms.

2. **Service Modifications**: We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.

3. **Termination by You**: You may cease using the Service at any time and, if applicable, cancel your subscription under the terms described in Section 5.3.

4. **Termination by Us**: We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason at our sole discretion, including any breach by you of these Terms.

---

## 13. Governing Law and Dispute Resolution

1. **Governing Law**: These Terms and any dispute arising under or relating to these Terms shall be governed by and construed in accordance with the laws of the **State of Wyoming**, without regard to its conflict of laws principles.

2. **Jurisdiction**: You agree to submit to the **exclusive jurisdiction of the state and federal courts** located in Wyoming for the resolution of any disputes arising out of or relating to these Terms or the Service.

3. **Waiver of Class Actions**: You agree that any disputes will be conducted only on an individual basis and not in a class, consolidated, or representative action.

---

## 14. Miscellaneous

1. **Entire Agreement**: These Terms, along with our Privacy Policy, constitute the entire agreement between you and Guernica LLC regarding the Service, superseding any prior or contemporaneous agreements.

2. **Severability**: If any provision of these Terms is held invalid or unenforceable, that provision will be enforced to the maximum extent permissible, and the remaining provisions will remain in full force and effect.

3. **No Waiver**: Our failure to enforce any right or provision in these Terms shall not constitute a waiver of such right or provision unless acknowledged and agreed to by us in writing.

4. **Assignment**: You may not assign or transfer these Terms or your obligations hereunder without our prior written consent. We may assign these Terms without restriction.

5. **Notices**: Any notices or communications required or permitted under these Terms shall be in writing and given by email or posted via the Service.

---

## 15. Contact Us

If you have any questions or concerns about these Terms, your account, or the Service, please contact us at:

**Guernica LLC**  
1309 Cofeen Ave, Sheridan, WY 82801, USA
contact@guernicaholdings.com`;

export default function Terms() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ 
        backgroundColor: 'background.paper',
        borderRadius: 1,
        p: 4,
        '& h1': { mb: 3 },
        '& h2': { mt: 4, mb: 2 },
        '& p': { mb: 2 },
        '& ul': { mb: 2 },
      }}>
        <ReactMarkdown>{tosContent}</ReactMarkdown>
      </Box>
    </Container>
  );
} 